import { useCallback, useEffect, useState } from 'react';
import { isUserHasKey } from '../../GlobalFunctions';
import DistrictSelector from '../AdminReportComponents/DistrictSelector';
import SchoolSelector from '../AdminReportComponents/SchoolSelector';
import { Icon } from '@iconify/react';
import { flushLocalForage, getLocalForage, removeLocalForage, setLocalForage } from '../../aaa';
import { useSelector } from 'react-redux';
import { RootState } from '../../ApplicationState';
import { getDistricts, getSchools, queryMixpanelByGroupV2 } from '../../BackendInterface';
import Spinner from 'react-spinkit';
import SpinnerOverlay from '../../SpinnerOverlay';
import { lookupNewDBStartDate } from '../reportUtilities';
import { District, ReportingMixpanelEvent, SchoolsList } from '../../../peekapak-types/ReportDataExchangeTypes';
import { SchoolModel } from '../../../peekapak-types/DataProtocolTypes';
import PeriodSelector from './PeriodSelector';
import ChartSegmentElementary from './ChartSegmentElementary';
import ChartSegmentSecondary from './ChartSegmentSecondary';
import styles from './MoodboardAdmin.module.scss';
import {
  getBeginningAndEndingOfFourWeek,
  getBeginningAndEndingOfQuarter,
  getBeginningAndEndingOfSemiYear,
  getBeginningAndEndingOfWeek,
  getBeginningAndEndingOfYear,
  getBeginningAndEndingOfSchoolYear,
  getBeginningAndEndingOfSchoolSemiYear,
  getBeginningAndEndingOfSchoolQuarter,
  getUpdatedDistrictUnifiedMoodScript,
  getUpdatedSchoolUnifiedMoodScript,
  parseSchoolJournalChartSchoolYearly,
} from './moodboardUtilities';
import { getDefaultSchoolYear } from '../reportUtilities';
import SchoolYearSelector from './SchoolYearSelector';

interface MoodboardAdminState {
  dataAvailabilityMessage: string;
  disableChangeLocation?: boolean;
  districtsList: District[] | Record<string, unknown>[];
  districtsListReady: boolean;
  errorMessage: string;
  mixpanelJournalMoodLogs: ReportingMixpanelEvent[];
  mixpanelJournalMoodLogsReady: boolean;
  schoolsList: SchoolsList;
  schoolsListReady: boolean;
  selectedDistrict: string;
  selectedSchool: string;
  selectedSchoolYear: string;
  selectedStartingDate: string;
  selectedEndingDate: string;
}

const tableOverrideSuffix = '';

const MoodboardDistrictAdmin = (): JSX.Element => {
  const defaultSchoolYear = getDefaultSchoolYear();

  const getCommonInitialState = () => {
    return {
      dataAvailabilityMessage: '',
      disableChangeLocation: true,
      errorMessage: '',
      mixpanelJournalMoodLogs: [],
      mixpanelJournalMoodLogsReady: false,
    };
  };

  const getDistrictInitialState = () => {
    return {
      districtsList: [],
      districtsListReady: false,
      selectedDistrict: '',
    };
  };

  const getSchoolInitialState = () => {
    return {
      schoolsList: {
        district: '',
        list: [],
      },
      schoolsListReady: false,
      selectedSchool: 'All',
    };
  };

  const getTimePeriodInitialState = () => {
    const { beginningTimeOfSchoolYear, endingTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(defaultSchoolYear);
    return {
      selectedSchoolYear: defaultSchoolYear,
      selectedStartingDate: beginningTimeOfSchoolYear.toISOString().split('T')[0],
      selectedEndingDate: endingTimeOfSchoolYear.toISOString().split('T')[0],
    };
  };

  const [state, setState] = useState<MoodboardAdminState>({
    ...getCommonInitialState(),
    ...getDistrictInitialState(),
    ...getSchoolInitialState(),
    ...getTimePeriodInitialState(),
  });

  //get beginning and ending time of each period
  const { beginningTimeOfSchoolYear, endingTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(
    state.selectedSchoolYear,
  );
  const { beginingTimeOfSemiYear, endingTimeOfSemiYear } = getBeginningAndEndingOfSchoolSemiYear(
    state.selectedSchoolYear,
  );
  const { beginingTimeOfQuarter, endingTimeOfQuarter } = getBeginningAndEndingOfSchoolQuarter(state.selectedSchoolYear);
  const [startYear] = state.selectedSchoolYear.split('-').map(Number);
  const { beginningTimeOfFourWeek, endingTimeOfFourWeek } = getBeginningAndEndingOfFourWeek(startYear);
  const { beginningTimeOfWeek, endingTimeOfWeek } = getBeginningAndEndingOfWeek(startYear);

  // initial date range of each period
  const initialYearlyRange = {
    from: beginningTimeOfSchoolYear,
    to: endingTimeOfSchoolYear,
  };
  const initialSemiYearlyRange = {
    from: beginingTimeOfSemiYear,
    to: endingTimeOfSemiYear,
  };
  const initiaQuarterlyRange = {
    from: beginingTimeOfQuarter,
    to: endingTimeOfQuarter,
  };
  const initialFourWeekRange = {
    from: beginningTimeOfFourWeek,
    to: endingTimeOfFourWeek,
  };
  const initialWeeklyRange = {
    from: beginningTimeOfWeek,
    to: endingTimeOfWeek,
  };

  const [dateRange, setDateRange] = useState(initialYearlyRange);
  const [selectedPeriodType, setSelectedPeriodType] = useState('yearly');
  const [elementaryJournals, setElementaryJournals] = useState([]);
  const [secondaryJournals, setSecondaryJournals] = useState([]);
  const [elementaryJournalsPrev, setElementaryJournalsPrev] = useState([]);
  const [secondaryJournalsPrev, setSecondaryJournalsPrev] = useState([]);
  const [journalsDataReady, setJournalsDataReady] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const userProfile = useSelector((state: RootState) => state.user.userProfile);

  const handleSetInitialDateRange = (period: string) => {
    switch (period) {
      case 'yearly':
        setDateRange(initialYearlyRange);
        break;
      case 'semi-yearly':
        setDateRange(initialSemiYearlyRange);
        break;
      case 'quarterly':
        setDateRange(initiaQuarterlyRange);
        break;
      case 'four-weekly':
        setDateRange(initialFourWeekRange);
        break;
      case 'weekly':
        setDateRange(initialWeeklyRange);
        break;
      default:
        break;
    }
  };

  const handleChangeTypeOfPeriod = (period: string) => {
    setSelectedPeriodType(period);
    handleSetInitialDateRange(period);
  };

  const getShiftedDateRange = (direction: string) => {
    const yearChange = direction === 'previous' ? -1 : 1;
    const semiYearChangeFrom = direction === 'previous' ? -6 : 6;
    const semiYearChangeTo = direction === 'previous' ? -5 : 7;
    const quarterChangeFrom = direction === 'previous' ? -3 : 3;
    const quarterChangeTo = direction === 'previous' ? -2 : 4;
    const fourWeekChange = direction === 'previous' ? -28 : 28;
    const weekChange = direction === 'previous' ? -7 : 7;

    switch (selectedPeriodType) {
      case 'yearly':
        const [currentStartYear, currentEndYear] = state.selectedSchoolYear.split('-').map(Number);
        const newSchoolYear =
          direction === 'previous'
            ? `${currentStartYear - 1}-${currentEndYear - 1}`
            : `${currentStartYear + 1}-${currentEndYear + 1}`;
        const { beginningTimeOfSchoolYear: newBeginning, endingTimeOfSchoolYear: newEnding } =
          getBeginningAndEndingOfSchoolYear(newSchoolYear);

        setDateRange({
          from: newBeginning,
          to: newEnding,
        });
        setState((prevState) => ({
          ...prevState,
          selectedSchoolYear: newSchoolYear,
          selectedStartingDate: newBeginning.toISOString().split('T')[0],
          selectedEndingDate: newEnding.toISOString().split('T')[0],
        }));
        break;
      case 'semi-yearly':
        setDateRange((prevState) => ({
          from: new Date(new Date(prevState.from).setMonth(new Date(prevState.from).getMonth() + semiYearChangeFrom)),
          to: new Date(new Date(prevState.to).setMonth(new Date(prevState.to).getMonth() + semiYearChangeTo, 0)),
        }));
        break;
      case 'quarterly':
        setDateRange((prevState) => ({
          from: new Date(new Date(prevState.from).setMonth(new Date(prevState.from).getMonth() + quarterChangeFrom)),
          to: new Date(new Date(prevState.to).setMonth(new Date(prevState.to).getMonth() + quarterChangeTo, 0)),
        }));
        break;
      case 'four-weekly':
        setDateRange((prevState) => {
          return {
            from: new Date(new Date(prevState.from).setDate(new Date(prevState.from).getDate() + fourWeekChange)),
            to: new Date(new Date(prevState.to).setDate(new Date(prevState.to).getDate() + fourWeekChange)),
          };
        });
        break;
      case 'weekly':
        setDateRange((prevState) => {
          return {
            from: new Date(new Date(prevState.from).setDate(new Date(prevState.from).getDate() + weekChange)),
            to: new Date(new Date(prevState.to).setDate(new Date(prevState.to).getDate() + weekChange)),
          };
        });
        break;
    }
  };

  const getPreviousDateRange = (currentDateRange: { from: Date; to: Date }) => {
    const semiYearChangeFrom = -6;
    const semiYearChangeTo = -5;
    const quarterChangeFrom = -3;
    const quarterChangeTo = -2;
    const fourWeekChange = -28;
    const weekChange = -7;

    let dateRange = {};

    switch (selectedPeriodType) {
      case 'semi-yearly':
        dateRange = {
          from: new Date(
            new Date(currentDateRange.from).setMonth(new Date(currentDateRange.from).getMonth() + semiYearChangeFrom),
          ),
          to: new Date(
            new Date(currentDateRange.to).setMonth(new Date(currentDateRange.to).getMonth() + semiYearChangeTo, 0),
          ),
        };
        break;
      case 'quarterly':
        dateRange = {
          from: new Date(
            new Date(currentDateRange.from).setMonth(new Date(currentDateRange.from).getMonth() + quarterChangeFrom),
          ),
          to: new Date(
            new Date(currentDateRange.to).setMonth(new Date(currentDateRange.to).getMonth() + quarterChangeTo, 0),
          ),
        };
        break;
      case 'four-weekly':
        dateRange = {
          from: new Date(
            new Date(currentDateRange.from).setDate(new Date(currentDateRange.from).getDate() + fourWeekChange),
          ),
          to: new Date(new Date(currentDateRange.to).setDate(new Date(currentDateRange.to).getDate() + fourWeekChange)),
        };
        break;
      case 'weekly':
        dateRange = {
          from: new Date(
            new Date(currentDateRange.from).setDate(new Date(currentDateRange.from).getDate() + weekChange),
          ),
          to: new Date(new Date(currentDateRange.to).setDate(new Date(currentDateRange.to).getDate() + weekChange)),
        };
        break;
    }
    return dateRange;
  };

  const shouldShowNavArrowRight = () => {
    const currentDate = new Date();
    const currentSchoolYear = getDefaultSchoolYear();

    if (selectedPeriodType === 'yearly') {
      return state.selectedSchoolYear < currentSchoolYear;
    }
    if (selectedPeriodType === 'semi-yearly' || selectedPeriodType === 'quarterly') {
      const [, endYear] = state.selectedSchoolYear.split('-').map(Number);
      if (dateRange.to.getFullYear() === currentDate.getFullYear()) {
        return dateRange.to.getMonth() < currentDate.getMonth();
      } else {
        return dateRange.to < currentDate;
      }
    }
    if (selectedPeriodType === 'four-weekly' || selectedPeriodType === 'weekly') {
      const { endingTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(state.selectedSchoolYear);
      return (endingTimeOfSchoolYear.getTime() - dateRange.to.getTime()) / 86400000 > 7 ? true : false;
    }
    return false;
  };

  const shouldShowNavArrowLeft = () => {
    if (selectedPeriodType === 'yearly') {
      return true;
    }
    if (selectedPeriodType !== 'yearly') {
      const { beginningTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(state.selectedSchoolYear);
      return (dateRange.from.getTime() - beginningTimeOfSchoolYear.getTime()) / 86400000 > 7 ? true : false;
    }
    return false;
  };

  //
  // get list of districts
  //
  useEffect(() => {
    let mounted = true;

    if (!state.districtsListReady) {
      getListOfDistricts();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getListOfDistricts() {
      const storageKey = 'districtsList';
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          districtsList: map,
          districtsListReady: true,
        }));
      }

      const mapFromDb = await getDistricts();
      await setLocalForage(storageKey, mapFromDb, 1440);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          districtsList: mapFromDb,
          districtsListReady: true,
        }));
      }
    }
  }, [state.districtsList, state.districtsListReady, userProfile?.district, state.selectedDistrict]);

  //
  // set selected district
  //
  useEffect(() => {
    run();

    async function run() {
      if (state.districtsListReady && state.districtsList.length > 0) {
        const storageKey = 'selectedDistrict';
        const selectedDistrict = await getLocalForage(storageKey);

        if (selectedDistrict) {
          setState((prevState) => ({ ...prevState, selectedDistrict }));
        } else {
          if (userProfile?.district) {
            if (state.districtsList.map((d) => d.id).includes(userProfile.district)) {
              return setState((prevState) => ({
                ...prevState,
                selectedDistrict: userProfile.district!,
              }));
            }
          }
        }
        return;
      }
      setState((prevState) => ({ ...prevState, selectedDistrict: '' }));
    }
  }, [userProfile?.district, state.districtsListReady, state.districtsList]);

  //
  // schools in district
  //
  useEffect(() => {
    let mounted = true;
    run();
    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (!state.schoolsListReady) await getListOfSchoolsInDistrict();
    }
    // load list of schools
    async function getListOfSchoolsInDistrict() {
      const districtStorageKey = 'selectedDistrict';
      const district =
        state.selectedDistrict || (await getLocalForage(districtStorageKey)) || userProfile?.district || '';
      if (!district) return;
      const storageKey = `${district}-schoolsList`;
      const list = (await getLocalForage(storageKey))?.filter((s: SchoolModel) => !s.isArchived);

      if (list?.length > 0 && mounted) {
        return setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list: [{ id: 'All', name: 'All', district: district }, ...list],
          },
          schoolsListReady: true,
        }));
      }

      const listFromDb = (await getSchools(district, tableOverrideSuffix))?.filter((s) => !s.isArchived);
      await setLocalForage(storageKey, listFromDb, 43200);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list: [{ id: 'All', name: 'All', district: district }, ...listFromDb],
          },
          schoolsListReady: true,
        }));
      }
    }
  }, [userProfile, state.schoolsListReady, state.selectedDistrict, state.districtsListReady]);

  //
  // set selected school
  //
  useEffect(() => {
    run();
    async function run() {
      if (state.selectedDistrict && state.schoolsListReady && state.schoolsList.list.length > 0) {
        const district = state.selectedDistrict || userProfile?.district;
        const storageKey = `${district}-selectedSchool`;
        const selectedSchool = await (async () => {
          // if user is not part of a district return nothing
          if (!userProfile?.district) {
            return '';
          }

          // if user is part of district and can change schools then return cache
          // or userProfile school Id
          if (isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')) {
            return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
          }

          // if user is part of district but cannot change schools, then return
          // userProfile's schoolId or blank
          return (await getLocalForage(storageKey)) || userProfile.schoolId || '';
        })();

        setState((prevState) => ({ ...prevState, selectedSchool }));
        if (selectedSchool !== 'All') {
          const schoolName = state.schoolsList.list.filter((s: SchoolModel) => s.id === selectedSchool)[0]?.name;
          setSchoolName(schoolName);
        }

        // if the user is part of Peekapak management and can select district then the user's profile's schoolId may be a part of a district that is not the currently selected district
        if (isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')) {
          if (state.schoolsListReady) {
            if (userProfile?.schoolId && state.schoolsList.list.map((s) => s.id).includes(userProfile.schoolId)) {
              return (await getLocalForage(storageKey)) || userProfile.schoolId;
            }

            return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
          }

          return '';
        }

        // if user is part of district and can change schools then return cache
        // or userProfile school Id
        if (isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')) {
          return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
        }

        // if user is part of district but cannot change schools, then return
        // userProfile's schoolId or blank
        return (await getLocalForage(storageKey)) || userProfile.schoolId || '';
      }

      setState((prevState) => ({ ...prevState, selectedSchool: 'All' }));
    }
  }, [
    state.schoolsListReady,
    state.schoolsList.list,
    state.selectedDistrict,
    userProfile?.district,
    userProfile?.schoolId,
  ]);

  //
  // get new mixpanel DB mood events for selected district
  //
  useEffect(() => {
    let mounted = true;
    const selectedStartingDate = state.selectedStartingDate;
    const selectedEndingDate = state.selectedEndingDate;
    const storageKey = `${state.selectedDistrict}-mixpanelJournalMoodLogs-from-${selectedStartingDate}-to-${selectedEndingDate}`;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (state.selectedDistrict && state.districtsListReady) {
        const data = await getLocalForage(storageKey);
        if (!data) {
          doQuery();
        } else {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              mixpanelJournalMoodLogs: data,
              mixpanelJournalMoodLogsReady: true,
            }));
          }
        }
      }
    }
    async function doQuery() {
      try {
        const data = await queryMixpanelByGroupV2(
          getUpdatedDistrictUnifiedMoodScript(selectedStartingDate, selectedEndingDate, state.selectedDistrict),
          'unified',
          tableOverrideSuffix,
        );
        await setLocalForage(storageKey, data, 60);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            mixpanelJournalMoodLogs: data,
            mixpanelJournalMoodLogsReady: true,
          }));
        }
      } catch (error) {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            errorMessage: (error as Error).message,
          }));
        }
      }
    }
  }, [state.selectedDistrict, state.selectedSchoolYear, state.selectedStartingDate, state.selectedEndingDate]);

  //
  // disable drop downs when data is being retrieved
  //
  useEffect(() => {
    if (
      (!state.districtsListReady || !state.mixpanelJournalMoodLogsReady || !state.schoolsListReady) &&
      !state.errorMessage
    ) {
      return setState((prevState) => ({
        ...prevState,
        disableChangeLocation: true,
      }));
    }

    return setState((prevState) => ({
      ...prevState,
      disableChangeLocation: false,
    }));
  }, [state.districtsListReady, state.schoolsListReady, state.mixpanelJournalMoodLogsReady, state.errorMessage]);

  //
  // set display of data availablity message
  //
  useEffect(() => {
    if (state.mixpanelJournalMoodLogsReady && !state.errorMessage) {
      const journalMoodLogStartDate = lookupNewDBStartDate('journalMoodLog');
      if (dateRange.from < journalMoodLogStartDate) {
        return setState((prevState) => ({
          ...prevState,
          dataAvailabilityMessage: 'Student mood data for this district is only available after August 2023.',
        }));
      }
    }
  }, [dateRange, selectedPeriodType, state.mixpanelJournalMoodLogs, state.mixpanelJournalMoodLogsReady]);

  //
  // set error conditions
  //
  useEffect(() => {
    if (userProfile && !userProfile.district && !isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (userProfile?.district && !isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL') && !userProfile.schoolId) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (state.schoolsListReady && state.schoolsList.list.length === 0) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No school data applicable',
      }));
    }
  }, [userProfile, state.districtsListReady, state.schoolsListReady, state.schoolsList, state.selectedDistrict]);

  const getMixpanelJournalMoodLogsForSchool = async (schoolId: string) => {
    const selectedStartingDate = state.selectedStartingDate;
    const selectedEndingDate = state.selectedEndingDate;

    const storageKey = `${schoolId}-mixpanelJournalMoodLogs-from-${selectedStartingDate}-to-${selectedEndingDate}`;

    if (schoolId && state.schoolsListReady) {
      const data = await getLocalForage(storageKey);
      if (!data) {
        doQuery();
      } else {
        setState((prevState) => ({
          ...prevState,
          mixpanelJournalMoodLogs: data,
          mixpanelJournalMoodLogsReady: true,
        }));
      }
    }
    async function doQuery() {
      const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === schoolId);

      if (!selectedSchoolObject)
        throw new Error(`Unexpected condition: could not find a matching school object for selected school in report`);

      try {
        const result = await queryMixpanelByGroupV2(
          getUpdatedSchoolUnifiedMoodScript(
            selectedStartingDate,
            selectedEndingDate,
            selectedSchoolObject.combineIds || selectedSchoolObject.id,
          ),
          'unified',
          tableOverrideSuffix,
        );

        await setLocalForage(storageKey, result, 60);

        setState((prevState) => ({
          ...prevState,
          mixpanelJournalMoodLogs: result,
          mixpanelJournalMoodLogsReady: true,
        }));
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          errorMessage: (error as Error).message,
        }));
      }
    }
  };

  const filterDataByDate = useCallback(async () => {
    if (state.mixpanelJournalMoodLogs && state.mixpanelJournalMoodLogsReady) {
      const journals = state.mixpanelJournalMoodLogs
        .filter((e) => new Date(e.createdAt) >= dateRange.from && new Date(e.createdAt) <= dateRange.to)
        .reduce(
          (acc, curr) => {
            acc[curr.grade <= 5 ? 'elementary' : 'secondary'].push(curr);
            return acc;
          },
          { elementary: [], secondary: [] },
        );
      setElementaryJournals(journals['elementary']);
      setSecondaryJournals(journals['secondary']);
      const dateRangePrevious = getPreviousDateRange(dateRange);
      const journalsLastPeriodToCompare = state.mixpanelJournalMoodLogs
        .filter((e) => new Date(e.createdAt) >= dateRangePrevious.from && new Date(e.createdAt) <= dateRangePrevious.to)
        .reduce(
          (acc, curr) => {
            acc[curr.grade <= 5 ? 'elementary' : 'secondary'].push(curr);
            return acc;
          },
          { elementary: [], secondary: [] },
        );
      setElementaryJournalsPrev(journalsLastPeriodToCompare.elementary);
      setSecondaryJournalsPrev(journalsLastPeriodToCompare.secondary);
      setJournalsDataReady(true);
    }
  }, [dateRange, selectedPeriodType, state.mixpanelJournalMoodLogs, state.mixpanelJournalMoodLogsReady]);

  useEffect(() => {
    filterDataByDate();
  }, [dateRange, selectedPeriodType, state.mixpanelJournalMoodLogs, state.mixpanelJournalMoodLogsReady]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getDistrictInitialState(),
      ...getSchoolInitialState(),
    }));
  }, [state.selectedSchoolYear, state.selectedStartingDate, state.selectedEndingDate]);

  async function onSelectDistrict(eventKey: string | null) {
    if (state.districtsListReady && state.districtsList.length > 0) {
      const selectedDistrict = eventKey || '';
      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        selectedDistrict,
      }));
      const storageKey = 'selectedDistrict';
      await setLocalForage(storageKey, selectedDistrict);
      await removeLocalForage(`${selectedDistrict}-selectedSchool`);
    } else {
      console.warn(`%cDistrict List is Missing`, 'background: black; color:yellow');
    }
  }

  async function onSelectSchool(eventKey: string) {
    if (eventKey && state.schoolsListReady) {
      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        selectedSchool: eventKey,
      }));
      const district = state.selectedDistrict || userProfile?.district;
      const storageKey = `${district}-selectedSchool`;
      await setLocalForage(storageKey, eventKey);
      if (eventKey === 'All') {
        const selectedStartingDate = state.selectedStartingDate;
        const selectedEndingDate = state.selectedEndingDate;
        const storageKeyAllSchools = `${state.selectedDistrict}-mixpanelJournalMoodLogs-from-${selectedStartingDate}-to-${selectedEndingDate}`;
        const data = await getLocalForage(storageKeyAllSchools);
        setState((prevState) => ({
          ...prevState,
          mixpanelJournalMoodLogs: data,
          mixpanelJournalMoodLogsReady: true,
        }));
        setSchoolName('');
      } else {
        getMixpanelJournalMoodLogsForSchool(eventKey);
      }
    }
  }

  async function onSelectSchoolYear(schoolYear: string) {
    const { beginningTimeOfSchoolYear, endingTimeOfSchoolYear } = getBeginningAndEndingOfSchoolYear(schoolYear);
    setState((prevState) => ({
      ...prevState,
      selectedSchoolYear: schoolYear,
      selectedStartingDate: beginningTimeOfSchoolYear.toISOString().split('T')[0],
      selectedEndingDate: endingTimeOfSchoolYear.toISOString().split('T')[0],
      ...getCommonInitialState(),
    }));
    setDateRange({
      from: beginningTimeOfSchoolYear,
      to: endingTimeOfSchoolYear,
    });
    setSelectedPeriodType('yearly');
  }

  async function onReload() {
    await flushLocalForage();
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getTimePeriodInitialState(),
      ...getDistrictInitialState(),
      ...getSchoolInitialState(),
    }));
  }

  if (!userProfile || !state.districtsListReady || !state.schoolsListReady || state.disableChangeLocation) {
    return <SpinnerOverlay isShow />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.centeringContainer}>
        <div className={styles.districtAndSchoolSelectors}>
          <div className={styles.titleAndReloadButton}>
            <h1>District Wide Mood Check-in</h1>
            <Icon className={styles.reloadIcon} icon='zondicons:reload' onClick={onReload} />
          </div>
          <DistrictSelector
            isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
            districtsList={state.districtsList}
            onReload={onReload}
            onSelectDistrict={onSelectDistrict}
            initialValue={state.selectedDistrict}
            currentlySelectedDistrict={state.selectedDistrict}
            disableChangeLocation={state.disableChangeLocation || false}
          />
          <SchoolSelector
            isDistrictAdmin={!!isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')}
            schoolsList={state.schoolsList}
            onSelectSchool={onSelectSchool}
            initialValue={state.selectedSchool}
            currentlySelectedSchool={state.selectedSchool}
            disableChangeLocation={state.disableChangeLocation || false}
          />
          <SchoolYearSelector
            selectedSchoolYear={state.selectedSchoolYear}
            onSelectSchoolYear={onSelectSchoolYear}
            disabled={state.disableChangeLocation || false}
          />
        </div>
        <PeriodSelector
          selectedPeriod={selectedPeriodType}
          handleClickPeriod={(period: string) => handleChangeTypeOfPeriod(period)}
          disableChangeTypeOfPeriod={state.disableChangeLocation || false}
        />
        {!state.mixpanelJournalMoodLogsReady || !journalsDataReady || state.disableChangeLocation ? (
          <div className={styles.loadingContainer}>
            <Spinner />
          </div>
        ) : (
          <div className={styles.chartsContainer}>
            <ChartSegmentElementary
              data={elementaryJournals}
              dataAvailabilityMessage={state.dataAvailabilityMessage}
              previousPeriodData={elementaryJournalsPrev}
              dateRange={dateRange}
              schoolName={schoolName}
              selectedPeriod={selectedPeriodType}
              shiftPeriod={(direction: string) => getShiftedDateRange(direction)}
              shouldShowNavArrowLeft={shouldShowNavArrowLeft}
              shouldShowNavArrowRight={shouldShowNavArrowRight}
            />
            <ChartSegmentSecondary
              data={secondaryJournals}
              dataAvailabilityMessage={state.dataAvailabilityMessage}
              previousPeriodData={secondaryJournalsPrev}
              dateRange={dateRange}
              schoolName={schoolName}
              selectedPeriod={selectedPeriodType}
              shiftPeriod={(direction: string) => getShiftedDateRange(direction)}
              shouldShowNavArrowLeft={shouldShowNavArrowLeft}
              shouldShowNavArrowRight={shouldShowNavArrowRight}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodboardDistrictAdmin;
